import React, { useMemo } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import clsx from 'clsx';

type EventType = {
  errors: string[] | string,
  extra_attr?: string,
  priority: string,
  uid: string,
};

interface EventsTableProps {
  events: EventType[];
  eventType: string;
}

const TABLE_BORDER_CLASSES = 'tw-border tw-border-collapse tw-border-solid';
const DEFAULT_BORDER = 'tw-border-med-grey';
const columnHelper = createColumnHelper<EventType>();

const EventsTable = ({ events, eventType }: EventsTableProps) => {
  const renderCell = cellData => <span>{cellData.getValue()}</span>;

  const extraAttrColumn = () => {
    let attr;
    let columnName;

    switch (eventType) {
      case 'teachers':
        attr = 'email';
        columnName = 'Email';
        break;
      case 'classrooms':
        attr = 'classroom_name';
        columnName = 'Class';
        break;
      case 'students':
        attr = 'username';
        columnName = 'Username';
        break;
      default:
        attr = 'extra_attr';
        columnName = 'Extra';
    }

    return (
      columnHelper.accessor(row => row.extra_attr, {
        cell: cellData => <span>{cellData.getValue()}</span>,
        header: columnName,
        id: attr,
      })
    );
  };

  const columns = useMemo(() => ([
    columnHelper.accessor(row => row.priority, {
      cell: info => <span>{info.getValue() === 'high' && <i className="fa fa-circle tw-text-red" /> }</span>,
      header: '',
      id: 'priority',
    }),
    columnHelper.accessor(row => row.uid, {
      cell: renderCell,
      header: 'ID',
      id: 'uid',
    }),
    extraAttrColumn(),
    columnHelper.accessor(row => row.errors, {
      cell: renderCell,
      header: 'Errors',
      id: 'errors',
    }),
  ]), [eventType]);

  const table = useReactTable({ columns, data: events, getCoreRowModel: getCoreRowModel() });

  const eventsRows = () => (
    <>
      <thead
        className={clsx({
          [DEFAULT_BORDER]: true,
          [TABLE_BORDER_CLASSES]: true,
          'tw-h-[1rem] tw-sticky tw-top-0 tw-bg-light-grey': true,
        })}
      >
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map(header => (
              <th
                key={header.id}
                className={clsx({
                  'tw-text-left tw-text-xs': true,
                  'tw-w-[15%]': ['classroom_name', 'email', 'username', 'uid'].includes(header.id),
                  'tw-w-[2%]': header.id === 'priority',
                  'tw-w-[66%]': header.id === 'errors',
                })}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row, index, allRows) => (
          <tr
            key={row.id}
            className={clsx({
              [DEFAULT_BORDER]: true,
              'tw-bg-[#FDF4F4]': row.original.priority === 'high',
              'tw-border-b': index !== allRows.length - 1,
              'tw-border-t tw-border-collapse tw-border-solid': true,
            })}
          >
            {row.getVisibleCells().map(cell => (
              <td
                key={cell.id}
                className={clsx({
                  'tw-p-2': cell.column.id === 'priority',
                  'tw-py-2 tw-pr-2 tw-align-middle': true,
                })}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </>
  );

  return (
    <div className="tw-max-h-[12rem] tw-overflow-auto tw-border tw-border-med-grey tw-border-solid">
      <table className="tw-w-full">
        {events.length > 0 ? eventsRows() : <p className="tw-pl-6 tw-my-2">NONE</p>}
      </table>
    </div>
  );
};

export default EventsTable;
